import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";

enum BillingProvider {
    ClinicDefault = 0,
    Medfar = 1,
    FacNet = 2,
    MDBilling = 3,
    Xacte = 4,
    Fmp = 5,
    Nestor = 6,
    XacteMultiD = 7,
    SoftInfo = 8,
    ClinicAid = 9
}

class BillingProviderHelper {
    /**
     * Gets the billing provider for the given value
     * @param value the billing provider value
     */
    public static getBillingProvider(value: string): BillingProvider | null {
        switch (value) {
            case null:
            case undefined:
                return BillingProvider.ClinicDefault;

            // empty string signifies the user intentionnaly disabled billing for their account
            case "":
                return null;

            // legacy saved value can be FMP, not Fmp
            case "FMP":
                return BillingProvider.Fmp;

            default:
                return BillingProvider[value] || BillingProvider.ClinicDefault;
        }
    }

    /**
     * Gets the billing provider for the given value, and resolves the default value if needed.
     * @param value the billing provider value
     */
    public static resolveBillingProvider(value: BillingProvider | string): BillingProvider | null {
        const provider = typeof value === "number" ? value : BillingProviderHelper.getBillingProvider(value);

        return provider === BillingProvider.ClinicDefault
            ? BillingProviderHelper.getDefaultBillingProvider()
            : provider;
    }

    /**
     * The clinic's default billing provider
     */
    public static getDefaultBillingProvider(): BillingProvider | null {
        const key = cacheStore.ClinicSettings.DefaultExternalBillingProvider;
        if (!key) {
            console.error("No default billing provider configured.");
            return null;
        }

        const provider: BillingProvider = key === "FMP"
            ? BillingProvider.Fmp
            : BillingProvider[key];

        if (!provider)
            console.error(`Default billing provider "${key}" is not valid.`);

        if (provider === BillingProvider.ClinicDefault) {
            console.error("Default billing provider cannot be 'ClinicDefault'.");

            return null;
        }

        return provider;
    }

    /**
     * Get a billing provider's display name.
     * @param value The billing provider.
     */
    public static getProviderDisplayName(value: BillingProvider): string {
        if (IsNullOrUndefined(value))
            return "";

        switch (EnumHelper.getValue(BillingProvider, value)) {
            case BillingProvider.FacNet:
                return "Facturation.net";

            case BillingProvider.Fmp:
                return "FMP";

            case BillingProvider.Xacte:
                return "X-Billing";

            case BillingProvider.XacteMultiD:
                return "X-Billing (Agence)";

            case BillingProvider.SoftInfo:
                return "SBilling";

            case BillingProvider.ClinicDefault:
                const clinicDefault = BillingProviderHelper.getDefaultBillingProvider();
                const label = cacheStore.GetString("ClinicDefaultLabel");

                return clinicDefault === BillingProvider.ClinicDefault
                    ? `${label} (${label})`
                    : `${label} (${BillingProviderHelper.getProviderDisplayName(clinicDefault)})`;

            default:
                return EnumHelper.getName(BillingProvider, value);
        }
    }

    /**
     * Get if a billing provider requires that the encounter is marked as a scheduled visit (SRV / RV)
     * @param value The billing provider.
     */
    public static getBillingProviderRequiresScheduledVisitCheck(value: BillingProvider): boolean {
        switch (EnumHelper.getValue(BillingProvider, value)) {
            case BillingProvider.FacNet:
            case BillingProvider.Fmp:
            case BillingProvider.Medfar:
                return true;

            default:
                return false;
        }
    }

    /**
     * Get whether billing provider supports prescription billing.
     * @param value The billing provider.
     */
    public static getBillingProviderSupportsPrescriptionRenewal(value: BillingProvider): boolean {
        if (IsNullOrUndefined(value))
            return false;

        switch (EnumHelper.getValue(BillingProvider, value)) {
            case BillingProvider.ClinicAid:
                return true;
            default:
                return false;
        }
    }

    /**
     * Get whether billing provider supports WorkSafe BC billing.
     * @param value The billing provider.
     */
    public static getBillingProviderSupportsWorkSafeBcForms(value: BillingProvider): boolean {
        if (IsNullOrUndefined(value))
            return false;

        switch (EnumHelper.getValue(BillingProvider, value)) {
            case BillingProvider.ClinicAid:
                return true;
            default:
                return false;
        }
    }

    /**
     * Get whether the billing provider supports employee select.
     * @param value The billing provider.
     */
    public static getBillingProviderSupportsEmployeeSelect(value: BillingProvider): boolean {
        if (IsNullOrUndefined(value))
            return false;

        switch (EnumHelper.getValue(BillingProvider, value)) {
            case BillingProvider.ClinicAid:
                return true;

            default:
                return false;
        }
    }

    /**
     * Get whether the billing provider supports using the PatientBillingHistory component in Medical Notes.
     * @param value The billing provider.
     */
    public static getBillingProviderSupportsPatientBillingHistory(value: BillingProvider): boolean {
        if (IsNullOrUndefined(value))
            return false;

        switch (EnumHelper.getValue(BillingProvider, value)) {
            case BillingProvider.MDBilling:
            case BillingProvider.ClinicAid:
                return true;

            default:
                return false;
        }
    }

    /**
     * Get whether the billing provider should use the legacy Medfar billing flow.
     * @param value The billing provider.
     */
    public static shouldUseLegacyMedfarBilling(value: BillingProvider): boolean {
        if (IsNullOrUndefined(value))
            return false;

        const provider = EnumHelper.getValue(BillingProvider, value);

        return provider === BillingProvider.Medfar
            || (provider === BillingProvider.Fmp && !cacheStore.ClinicSettings.EnableNewFmpBillingIntegration);
    }

    /**
     * Get whether the billing provider should use the legacy Facturation.Net billing flow.
     * @param value The billing provider.
     */
    public static shouldUseLegacyFacturationNetBilling(value: BillingProvider): boolean {
        if (IsNullOrUndefined(value))
            return false;

        return EnumHelper.getValue(BillingProvider, value) === BillingProvider.FacNet;
    }

    /**
     * Get whether the current employee is billable for the given provider.
     * @param value The billing provider.
     * @param employee The employee.
     */
    public static getEmployeeIsBillableForProvider(value: BillingProvider, employee: Employee, employeeModelRegionCode?: string): boolean {
        if (IsNullOrUndefined(value))
            return false;

        switch (this.resolveBillingProvider(value)) {
            case BillingProvider.Medfar:
            case BillingProvider.Fmp:
                return !IsNullOrWhiteSpace(employee.RamqNumber)
                    && employee.RamqNumber === cacheStore.GetCurrentEmployee().RamqNumber;

            case BillingProvider.Xacte:
            case BillingProvider.XacteMultiD:
            case BillingProvider.Nestor:
                return !IsNullOrWhiteSpace(employee.RamqNumber);

            case BillingProvider.ClinicAid:
                if (employeeModelRegionCode === "CA-BC") {
                    return !IsNullOrWhiteSpace(employee.ProfessionalNumber)
                    && !IsNullOrWhiteSpace(employee.BillingAgencyNumber);
                }

                return !IsNullOrWhiteSpace(employee.ProfessionalNumber);
            default:
                return false;
        }
    }

    /**
     * Get whether the current employee is billable for the given provider.
     * @param value The billing provider.
     */
    public static getCurrentEmployeeIsBillableForProvider(value: BillingProvider): boolean {
        const employee = cacheStore.GetCurrentEmployee();
        const employeeModelRegionCode = EmployeeUIModel.fromCompositeObject(employee).getBillingRegionIsoCode();

        return this.getEmployeeIsBillableForProvider(value, employee, employeeModelRegionCode);
    }

    /**
     * Determines if the given billing provider is deprecated.
     *
     * @param billingProvider - The billing provider to check.
     * @returns `true` if the billing provider is deprecated, otherwise `false`.
     */
    public static IsDeprecatedBillingProviderThatDisplaysHistoricalClaims(billingProvider: BillingProvider): boolean {
        return billingProvider === BillingProvider.MDBilling;
    }
}

export { BillingProvider, BillingProviderHelper };
